/** E14.5 Kundenbindung: Newsletter mit Double-Opt-in und die öffentliche Seite „Neu im Laden“. */
import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Notice, Seitenzustand } from "../ui";
import { Card, Dl, KpiTile, PageHeader, Textarea } from "../ui/Layout";
import { TextField } from "../ui";
import { api, rufe } from "../api/client";
import { formatDatumZeit } from "@shared/zeit";
import type { LadenEinstellungen } from "@shared/schemas/laden";
import { useLaden } from "./api";
import { useNewsletter, useNewsletterSenden } from "./wachstum-api";

export function Newsletter() {
  const laden = useLaden();
  const stand = useNewsletter();
  const senden = useNewsletterSenden();
  const qc = useQueryClient();
  const speichern = useMutation({ mutationFn: (neu: LadenEinstellungen) => rufe(api.api.laden.einstellungen.$put({ json: neu })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
  const [form, setForm] = useState({ betreff: "", text: "", mitNeuheiten: true });
  const [hinweis, setHinweis] = useState<string | null>(null);
  const e = laden.data?.einstellungen;
  const slug = laden.data?.slug ?? "";
  const kann = (laden.data ? true : false) && true;

  function umschalten(feld: "newsletter" | "neuImLaden", wert: boolean) {
    if (!e) return;
    speichern.mutate({ ...e, kunden: { ...e.kunden, [feld]: wert } });
  }
  function absenden(ev: FormEvent) {
    ev.preventDefault();
    if (!window.confirm(`Newsletter „${form.betreff}“ jetzt an alle bestätigten Empfänger senden?`)) return;
    senden.mutate(form, { onSuccess: (r) => { setHinweis(`Gesendet an ${r.anzahl} Empfänger.`); setForm({ betreff: "", text: "", mitNeuheiten: true }); } });
  }

  return (
    <>
      <PageHeader kicker="Mieter" title="Kunden und Newsletter" sub="Öffentliche Seite mit frischen Artikeln und ein Newsletter für Stammkundschaft (Anmeldung nur mit Bestätigung per E-Mail)" />
      {hinweis ? <Notice tone="success">{hinweis}</Notice> : null}
      <Seitenzustand query={stand}>
        {(d) => (
          <div className="sc-col" style={{ gap: "var(--space-4)" }}>
            <Card title="Öffentliche Seite „Neu im Laden“">
              <label className="sc-check"><input type="checkbox" checked={d.neuImLaden} onChange={(ev) => umschalten("neuImLaden", ev.target.checked)} disabled={!kann} /> Seite anzeigen: neue Artikel der letzten 14 Tage aus freigegebenen Regalen, kommende Events, Öffnungszeiten</label>
              {d.neuImLaden && slug ? <p className="sc-muted sc-small">Adresse zum Teilen: <a href={`/neu/${slug}`} target="_blank" rel="noreferrer">{window.location.origin}/neu/{slug}</a></p> : null}
            </Card>
            <Card title="Newsletter">
              <label className="sc-check"><input type="checkbox" checked={d.aktiv} onChange={(ev) => umschalten("newsletter", ev.target.checked)} disabled={!kann} /> Anmeldung auf der Seite „Neu im Laden“ anbieten</label>
              <div className="sc-kpis">
                <KpiTile label="Empfänger" value={d.bestaetigt} note="bestätigt" />
                <KpiTile label="Unbestätigt" value={d.offen} />
                <KpiTile label="Abgemeldet" value={d.abgemeldet} />
              </div>
            </Card>
            <Card title="Newsletter senden">
              <form className="app-form" onSubmit={absenden} noValidate>
                <TextField label="Betreff" value={form.betreff} onChange={(ev) => setForm({ ...form, betreff: ev.target.value })} maxLength={120} />
                <Textarea label="Text" value={form.text} onChange={(ev) => setForm({ ...form, text: ev.target.value })} rows={8} maxLength={5000} hint="Reiner Text. Abmeldelink und Ladenname werden automatisch angehängt." />
                <label className="sc-check"><input type="checkbox" checked={form.mitNeuheiten} onChange={(ev) => setForm({ ...form, mitNeuheiten: ev.target.checked })} /> Bis zu acht neue Artikel und den Link zur Seite „Neu im Laden“ anhängen</label>
                {senden.isError ? <Notice tone="error">{senden.error.message}</Notice> : null}
                <div className="app-form__actions">
                  <Button type="submit" variant="primary" icon="mail" loading={senden.isPending} disabled={form.betreff.trim().length < 2 || form.text.trim().length < 2 || d.bestaetigt === 0}>An {d.bestaetigt} Empfänger senden</Button>
                </div>
              </form>
            </Card>
            {d.versand.length ? (
              <Card title="Bisherige Aussendungen">
                <Dl rows={d.versand.map((v) => [formatDatumZeit(v.erstelltAt), `${v.betreff} · ${v.anzahl} Empfänger`])} />
              </Card>
            ) : null}
          </div>
        )}
      </Seitenzustand>
    </>
  );
}
